import "./userDashboard.css";
import { useState, useEffect, useRef } from "react";

import { CreativeFilter } from "../../components/atoms/creativeFilter";
import { SelectInputBox } from "../../components/atoms/selectInput";
import { Header } from "../../components/molecules/header";
import { SidebarUser } from "../../components/molecules/sidebar";
import { UserCardSection } from "../../components/organisms/userCardSection";
import { authDetails } from "../../api/auth/authActions";
import { useNavigate } from "react-router-dom";
import { getAuthFromLocal } from "../../utils/storage";
import { useDispatch, useSelector } from "react-redux";
import { storeHandler } from "../../api/store/storeActions";

function UserDashboard() {

  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  let data: authDetails = getAuthFromLocal();
  !data && navigate("/");

  const [length, setLength] = useState<number>();
  const [isLoading, setIsLoading] = useState(true);
  const [regex, setRegex] = useState<string>("");
  const [keyword, setKeyword] = useState<string>("");
  const [order, setOrder] = useState<string>("1");
  const [pageNo, setPageNo] = useState(1);
  const [showMenu, setShowMenu] = useState(false);
  const prevMediaTypeRef = useRef<string[]>([]);

  // Restore persisted state on mount
  useEffect(() => {
    const savedSuggestion = localStorage.getItem('lastSelectedSuggestion');
    const savedMetadata = localStorage.getItem('lastSelectedMetadata');
    const savedCreativeIds = localStorage.getItem('selectedCreativeIds');
    const savedMediaType = localStorage.getItem('selectedMediaType');
    
    if (savedSuggestion) {
      try {
        const parsed = JSON.parse(savedSuggestion);
        const keywordToRestore = parsed.title || parsed.value || "";
        if (keywordToRestore) {
          setKeyword(keywordToRestore);
          setRegex(keywordToRestore);
        }
      } catch (e) {
        console.error('Failed to restore saved suggestion:', e);
      }
    }
    
    if (savedMetadata) {
      try {
        const metadata = JSON.parse(savedMetadata);
        dispatch(storeHandler(metadata));
      } catch (e) {
        console.error('Failed to restore saved metadata:', e);
      }
    }
    
    if (savedCreativeIds) {
      try {
        const creativeIds = JSON.parse(savedCreativeIds);
        if (Array.isArray(creativeIds) && creativeIds.length > 0) {
          dispatch(storeHandler({ selectedCreativeIds: creativeIds }));
        }
      } catch (e) {
        console.error('Failed to restore saved creative IDs:', e);
      }
    }
    
    if (savedMediaType) {
      try {
        const mediaType = JSON.parse(savedMediaType);
        if (Array.isArray(mediaType) && mediaType.length > 0) {
          dispatch(storeHandler({ mediaType }));
        }
      } catch (e) {
        console.error('Failed to restore saved media type:', e);
      }
    }
  }, [dispatch]);

  // Get all filter states from Redux
  const adType = useSelector((state: any) => state.data.adType) || [];
  const brand = useSelector((state: any) => state.data.brand) || [];
  const category = useSelector((state: any) => state.data.category) || [];
  const country = useSelector((state: any) => state.data.country) || [];
  const dtLevel = useSelector((state: any) => state.data.dtLevel) || [];
  const feature = useSelector((state: any) => state.data.feature) || [];
  const language = useSelector((state: any) => state.data.language) || [];
  const orientation = useSelector((state: any) => state.data.orientation) || [];
  const region = useSelector((state: any) => state.data.region) || [];
  const stage = useSelector((state: any) => state.data.stage) || [];
  const subBrand = useSelector((state: any) => state.data.subBrand) || [];
  const brandCollab = useSelector((state: any) => state.data.brandCollab) || [];
  const year = useSelector((state: any) => state.data.year) || [];
  const mediaType = useSelector((state: any) => state.data.mediaType) || [];

  // Function to handle individual filter removal
  const handleRemoveFilter = (filterType: string, item: any) => {
    setPageNo(1);
    setIsLoading(true);

    // Handle mediaType separately - it's not part of getCurrentFilter
    if (filterType === 'mediaType') {
      const updatedFilter = mediaType.filter((filterItem: string) => filterItem !== item);
      dispatch(storeHandler({ [filterType]: updatedFilter }));
      return;
    }

    // Remove the specific item from the filter array
    const currentFilter = getCurrentFilter(filterType);
    const updatedFilter = currentFilter.filter((filterItem: any) => filterItem.title !== item.title);

    // Dispatch the updated filter
    const data = {
      [filterType]: updatedFilter,
    };
    dispatch(storeHandler(data));
  };

  // Helper function to get current filter array (mediaType is separate, not included here)
  const getCurrentFilter = (filterType: string) => {
    switch (filterType) {
      case 'adType': return adType;
      case 'brand': return brand;
      case 'category': return category;
      case 'country': return country;
      case 'dtLevel': return dtLevel;
      case 'feature': return feature;
      case 'language': return language;
      case 'orientation': return orientation;
      case 'region': return region;
      case 'stage': return stage;
      case 'subBrand': return subBrand;
      case 'brandCollab': return brandCollab;
      case 'year': return year;
      default: return [];
    }
  };

  // Reset page number and show loading when mediaType filter changes
  useEffect(() => {
    // Only reset if mediaType actually changed (not on initial mount)
    const isInitialMount = prevMediaTypeRef.current.length === 0 && mediaType.length === 0;
    const mediaTypeChanged = JSON.stringify(prevMediaTypeRef.current) !== JSON.stringify(mediaType);
    
    if (mediaTypeChanged && !isInitialMount) {
      setPageNo(1);
      setIsLoading(true);
      // Clear existing creatives immediately when filter changes
      dispatch(storeHandler({ creative: [] }));
      setLength(undefined);
    }
    prevMediaTypeRef.current = mediaType;
  }, [mediaType, dispatch]);

  if (!data) {
    return <Header setRegex={setRegex} setKeyword={setKeyword} setPageNo={setPageNo} regex={regex} setShowMenu={setShowMenu} showMenu={showMenu} onRemoveFilter={handleRemoveFilter} />; // Or a loading spinner, or a minimal component that doesn't trigger API calls
  }

  return (
    <div className="relative">
      <Header setRegex={setRegex} setKeyword={setKeyword} setPageNo={setPageNo} regex={regex} setShowMenu={setShowMenu} showMenu={showMenu} onRemoveFilter={handleRemoveFilter} />
      <div className="flex min-h-[90vh]">
        <SidebarUser
          regex={keyword}
          pageNo={pageNo}
          setPageNo={setPageNo}
          setRegex={setRegex}
          setKeyword={setKeyword}
          setIsLoading={setIsLoading}
          showMenu={showMenu}
          setShowMenu={setShowMenu}
        />
        <div className="w-full lg:w-[80%]">
          <div className="lg:w-full w-[100vw] p-4 px-8 flex flex-col lg:flex-row gap-4 items-center justify-end">
            <div className="flex items-center gap-2">
              <span className="capitalize text-sm">Sort:</span>
              <SelectInputBox
                items={[
                  { value: "1", name: "latest" },
                  { value: "2", name: "oldest" },
                  { value: "3", name: "created at" },
                ]}
                value={order}
                onChange={(value: string) => setOrder(value)}
              />
            </div>
          </div>
          <div className="px-8 pb-4">
            <h1 className="text-[1.4rem] font-semibold">
              {isLoading ? <></> : (length ? `Found ${length} creatives` : "All Creatives")}
            </h1>
          </div>
          <UserCardSection
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            regex={keyword}
            length={length}
            sortOrder={(order === "1" || order === "3") ? -1 : 1}
            sortType={order === "1" ? "year" : order === "2" ? "year" : "createdAt"}
            pageNo={pageNo}
            setPageNo={setPageNo}
            setLength={setLength}
          />
        </div>
      </div>
    </div>
  );
}

export default UserDashboard;
